/* eslint-disable no-param-reassign */
import { flow, getParent, hasParent, onAction, getSnapshot } from 'mobx-state-tree';
import { getIdentifier } from '~/mst/utils';

import api from '~/api';
import type { IDashboardsModel } from '~/mst/models/dashboards/model';
import { WidgetTypes } from '~/mst/models/widget/types';

import { IDashboardModel, DASHBOARD_NAME_MAX_LENGTH } from './model';

const fetchWidgets = [
  WidgetTypes.ChartWidget,
  WidgetTypes.WindRoseWidget,
  WidgetTypes.ConnectivityWidget,
  WidgetTypes.MapWidget,
  WidgetTypes.DistributionWidget
];

export default (self: IDashboardModel) => ({
  // Only the organization and the owner change, so they're set in place: the response keeps
  // widgets and range in `configuration`, which applying it whole here would lose, and reloading
  // the list would skip the selected organization.
  share: flow(function* share(external_org_id) {
    self.startSyncing();
    try {
      const { data } = yield api.dashboards.share(getIdentifier(self)!, external_org_id);
      self.organization_id = data.organization_id;
      self.user_id = data.user_id;
      self.finishSyncing();
    } catch (error) {
      self.failSyncing(error);
    }
  }),
  // the dashboard becomes the current user's own; throws, so the caller can tell it failed.
  // Only its owner changes (see share).
  unshare: flow(function* unshare() {
    self.startSyncing();
    try {
      const { data } = yield api.dashboards.unshare(getIdentifier(self)!);
      self.user_id = data.user_id;
      self.finishSyncing();
    } catch (error) {
      self.failSyncing(error);
      throw error;
    }
  }),
  fetch() {
    self.widgets.toArray.filter((widget) => fetchWidgets.includes(widget.type)).forEach((widget) => widget.fetch(self.range.fetchParams()));
  },
  clone: flow(function* clone() {
    self.startSyncing();
    try {
      // the name cut so that " (copy)" still fits
      const suffix = ' (copy)';
      const payload = {
        name: `${(self.name || '').slice(0, DASHBOARD_NAME_MAX_LENGTH - suffix.length)}${suffix}`
      };
      const { data } = yield api.dashboards.clone(getIdentifier(self)!, payload);
      const dashboards = getParent<IDashboardsModel>(self, 2);
      dashboards.add(data);
      self.finishSyncing();
      return data;
    } catch (error) {
      self.failSyncing(error);
      throw error;
    }
  }),
  setRange(range) {
    self.range = range;
  },
  toggleUpdates() {
    self.is_updates_on = !self.is_updates_on;
    if (self.is_updates_on) {
      self.fetch();
    }
  },
  toggleIsStarred() {
    self.is_starred = !self.is_starred;
  },
  updateWidgets(layouts, breakpoint) {
    layouts.forEach((layout) => {
      const widget = self.widgets.getById(layout.i);
      if (widget) {
        widget.layout.setBreakpoint(breakpoint, layout);
      }
    });
  },
  update: flow(function* update(values) {
    try {
      yield api.dashboards.update(getIdentifier(self)!, values || getSnapshot(self));
      if (values) {
        Object.assign(self, values);
      }
      // saved from this dashboard's own onAction (a star, the range, the layout): the list never
      // sees an `update` to drop its cached response, and a reload would put the old one back
      if (hasParent(self, 2)) {
        getParent<IDashboardsModel>(self, 2).resetCache();
      }
    } catch (error) {
      self.failSyncing(error);
    }
  }),
  afterCreate() {
    onAction(
      self,
      (action) => {
        const { name } = action;
        // only what the user may edit is saved; a read-only user's range stays theirs, in the page
        if (['setRange', 'updateWidgets', 'toggleIsStarred'].includes(name) && self.canEdit) {
          self.update();
        }
        if (name === 'setRange') {
          self.fetch();
        }
      },
      true
    );
  }
});
